import axios from "axios";
import Config from "config";
import { Dispatch } from "redux";
import { SeverityLevel, ToastService } from "services/toastService";
import { AuditLog } from "./request_details_dependencies";
import { getStepFromStatus } from "utils/helpers";

// Types
export interface Request {
  requestId: string;
  requestType: string;
  requestBy: string;
  requestedFor: string;
  requestRaisedOn: string;
  status:
    | "OPEN"
    | "PENDING_REVIEW"
    | "APPROVED"
    | "REJECTED"
    | "WORK_IN_PROGRESS"
    | "COMPLETED"
    | "CANCELLED";
}

export interface CreateRequest {
  requestType: string;
  requestedFor: string;
}

export interface CreateRequestPayload {
  requestType: string | any;
  context: any;
  data: any;
}

export type GetRequestDetailByEmail = {
  email: string;
  requestId: string;
};

const defaultAnonymousRequestData = {
  id: null,
  data: null,
  email: null,
  requestId: null,
  createdAt: null,
  requestType: null,
  requestText: null,
  currentStep: 1,
  fromStatusCode: null,
  toStatusCode: null,
  workInProgressBy: null,
  currentApproverRole: null,
};

export interface RequestState {
  content: Request[];
  loading: boolean;
  error: string | null;
  totalPages: number;
  currentPage: number;
  total: number;
  creating: boolean;
  anonymousRequest: {
    loading: boolean;
    error: string | null;
    requestId: string | null;
  };
  anonymousRequestData: {
    loading: boolean;
    error: string | null;
    data: {
      id: number | null;
      requestId: string | null;
      email: string | null;
      data: string | null;
      createdAt: string | null;
      requestType: string | null;
      requestText: string | null;
      currentStep: number;
      toStatusCode: string | null;
      fromStatusCode: string | null;
      workInProgressBy: string | null;
      currentApproverRole: string | null;
    };
  };
  auditlogs: {
    loading: boolean;
    error: string | null;
    data: Array<AuditLog>;
  };
  createError: string | null;
  showUserCreationModal: boolean;
  showSearchEmailModal: boolean;
  showRequestDetailsModal: boolean;
}

// Action Types
const FETCH_REQUEST = "FETCH_REQUEST";
const FETCH_REQUEST_SUCCESS = "FETCH_REQUEST_SUCCESS";
const FETCH_REQUEST_FAILURE = "FETCH_REQUEST_FAILURE";

const CREATE_REQUEST = "CREATE_REQUEST";
const CREATE_REQUEST_SUCCESS = "CREATE_REQUEST_SUCCESS";
const CREATE_REQUEST_FAILURE = "CREATE_REQUEST_FAILURE";

const SHOW_USER_CREATION_MODAL = "SHOW_USER_CREATION_MODAL";
const SHOW_SEARCH_EMAIL_MODAL = "SHOW_SEARCH_EMAIL_MODAL";
const SHOW_REQUEST_DETAILS_MODAL = "SHOW_REQUEST_DETAILS_MODAL";
const HIDE_REQUEST_DETAILS_MODAL = "HIDE_REQUEST_DETAILS_MODAL";
const HIDE_USER_CREATION_MODAL = "HIDE_USER_CREATION_MODAL";
const HIDE_SEARCH_EMAIL_MODAL = "HIDE_SEARCH_EMAIL_MODAL";
const CREATE_ANONYMOUS_REQUEST = "CREATE_ANONYMOUS_REQUEST";
const CREATE_ANONYMOUS_REQUEST_SUCCESS = "CREATE_ANONYMOUS_REQUEST_SUCCESS";
const CREATE_ANONYMOUS_REQUEST_FAILURE = "CREATE_ANONYMOUS_REQUEST_FAILURE";
const FETCH_REQUEST_DETAILS_BY_EMAIL = "FETCH_REQUEST_DETAILS_BY_EMAIL";
const FETCH_REQUEST_DETAILS_BY_EMAIL_SUCCESS = "FETCH_REQUEST_DETAILS_BY_EMAIL_SUCCESS";
const FETCH_REQUEST_DETAILS_BY_EMAIL_FAILURE = "FETCH_REQUEST_DETAILS_BY_EMAIL_FAILURE";

const FETCH_ANONYMOUS_AUDITLOG_DETAILS = "FETCH_ANONYMOUS_AUDITLOG_DETAILS";
const FETCH_ANONYMOUS_AUDITLOG_DETAILS_SUCCESS = "FETCH_ANONYMOUS_AUDITLOG_DETAILS_SUCCESS";
const FETCH_ANONYMOUS_AUDITLOG_DETAILS_FAILURE = "FETCH_ANONYMOUS_AUDITLOG_DETAILS_FAILURE";

// Action Creators
export const fetchRequests = (
  page: number = 1,
  limit: number = 100,
  selectedStatus?: string,
  selectedRequestType?: string,
  requestIdSearch?: string,
  emailSearch?: string
) => {
  return async (dispatch: Dispatch) => {
    dispatch({ type: FETCH_REQUEST });
    try {
      // Build params object, only including non-empty filters
      const params: any = {
        page,
        limit,
      };
      // Only add filter params if they're not empty
      if (selectedStatus && selectedStatus !== "all") {
        params.selectedStatus = selectedStatus;
      }
      if (selectedRequestType && selectedRequestType !== "all") {
        params.selectedRequestType = selectedRequestType;
      }
      if (requestIdSearch && requestIdSearch.trim() !== "") {
        params.requestIdSearch = requestIdSearch.trim();
      }
      if (emailSearch && emailSearch.trim() !== "") {
        params.emailSearch = emailSearch.trim();
      }

      const response = await axios.get(`${Config.API_SERVICE_URL}/api/request`, { params });

      // Handle both array response and paginated response
      const content = Array.isArray(response.data)
        ? response.data
        : response.data.content || response.data.data || [];
      const totalPages = response.data.totalPages || response.data.total_pages || 1;
      const currentPage = response.data.currentPage || response.data.current_page || page;
      const total =
        response.data.total || (Array.isArray(response.data) ? response.data.length : 0);

      dispatch({
        type: FETCH_REQUEST_SUCCESS,
        payload: {
          content,
          totalPages,
          currentPage,
          total,
        },
      });
    } catch (error: any) {
      console.error("Error fetching request approvals:", error);
      dispatch({
        type: FETCH_REQUEST_FAILURE,
        payload: error.message || "Failed to fetch request approvals",
      });
    }
  };
};

export const createRequest = (payload: CreateRequestPayload) => {
  return async (dispatch: Dispatch) => {
    dispatch({ type: CREATE_REQUEST });

    try {
      const response = await axios.post(`${Config.API_SERVICE_URL}/api/request`, payload);

      const newRequest: CreateRequest = {
        requestType: response.data.request_type || response.data.requestType,
        requestedFor: response.data.requested_for || response.data.requestedFor,
      };

      dispatch({
        type: CREATE_REQUEST_SUCCESS,
        payload: newRequest,
      });

      ToastService.getInstance().publishToast(
        {
          identifier: "success-operation",
          title: "Success",
          message: "Request created successfully!",
        },
        SeverityLevel.SUCCESS,
        { autoClose: 3000 }
      );

      return newRequest;
    } catch (error: any) {
      console.error("Error creating request approval:", error);
      dispatch({
        type: CREATE_REQUEST_FAILURE,
        payload: error.message || "Failed to create request approval",
      });
      throw error;
    }
  };
};
export const createAnonymousRequest = (payload: CreateRequestPayload) => {
  return async (dispatch: Dispatch) => {
    dispatch({ type: CREATE_ANONYMOUS_REQUEST });

    try {
      const response = await axios.post(
        `${Config.API_SERVICE_URL}/api/CreateAnonymousRequest`,
        payload
      );

      const newRequestId: string = response.data.display_request_id;

      dispatch({
        type: CREATE_ANONYMOUS_REQUEST_SUCCESS,
        payload: newRequestId,
      });

      return newRequestId;
    } catch (error: any) {
      console.error("Error creating anonymous request:", error);
      dispatch({
        type: CREATE_ANONYMOUS_REQUEST_FAILURE,
        payload: error.message || "Failed to create anonymous request",
      });
      throw error;
    }
  };
};
export const fetchRequestDetailByEmail = (payload: GetRequestDetailByEmail) => {
  return async (dispatch: Dispatch) => {
    dispatch({ type: FETCH_REQUEST_DETAILS_BY_EMAIL });
    const { email, requestId } = payload;
    try {
      const response = await axios.get(
        `${Config.API_SERVICE_URL}/api/getRequestByEmail?email=${email}&requestId=${requestId}`
      );

      dispatch({
        type: FETCH_REQUEST_DETAILS_BY_EMAIL_SUCCESS,
        payload: response.data,
      });
      return response.data;
    } catch (error: any) {
      dispatch({
        type: FETCH_REQUEST_DETAILS_BY_EMAIL_FAILURE,
        payload: error.message || "Failed to fetch request details",
      });
      throw error;
    }
  };
};

export const fetchAnonymousAuditlog = (id: number) => {
  return async (dispatch: Dispatch) => {
    dispatch({ type: FETCH_ANONYMOUS_AUDITLOG_DETAILS });
    try {
      const response = await axios.get(
        `${Config.API_SERVICE_URL}/api/auditRequestApprovalLog/${id}`
      );

      dispatch({
        type: FETCH_ANONYMOUS_AUDITLOG_DETAILS_SUCCESS,
        payload: response.data,
      });
      return response.data;
    } catch (error: any) {
      dispatch({
        type: FETCH_ANONYMOUS_AUDITLOG_DETAILS_SUCCESS,
        payload: error.message || "Failed to fetch auditlog details",
      });
      throw error;
    }
  };
};

export const showUserCreationModal = () => ({
  type: SHOW_USER_CREATION_MODAL,
});

export const showSearchEmailModal = () => ({
  type: SHOW_SEARCH_EMAIL_MODAL,
});

export const showRequestDetailsModal = () => ({
  type: SHOW_REQUEST_DETAILS_MODAL,
});

export const hideRequestDetailsModal = () => ({
  type: HIDE_REQUEST_DETAILS_MODAL,
});

export const hideSearchEmailModal = () => ({
  type: HIDE_SEARCH_EMAIL_MODAL,
});

export const hideUserCreationModal = () => ({
  type: HIDE_USER_CREATION_MODAL,
});

// Initial State
const initialState: RequestState = {
  anonymousRequest: {
    loading: false,
    error: null,
    requestId: null,
  },
  anonymousRequestData: {
    loading: false,
    error: null,
    data: { ...defaultAnonymousRequestData },
  },
  auditlogs: {
    loading: false,
    error: null,
    data: [],
  },
  content: [],
  loading: false,
  error: null,
  totalPages: 0,
  currentPage: 1,
  total: 0,
  creating: false,
  createError: null,
  showUserCreationModal: false,
  showSearchEmailModal: false,
  showRequestDetailsModal: false,
};

// Reducer
export const requestReducer = (state = initialState, action: any): RequestState => {
  switch (action.type) {
    case FETCH_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };

    case FETCH_REQUEST_SUCCESS:
      return {
        ...state,
        loading: false,
        content: action.payload.content,
        totalPages: action.payload.totalPages,
        currentPage: action.payload.currentPage,
        total: action.payload.total,
        error: null,
      };

    case FETCH_REQUEST_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
      };

    case CREATE_REQUEST:
      return {
        ...state,
        creating: true,
        createError: null,
      };

    case CREATE_REQUEST_SUCCESS:
      return {
        ...state,
        creating: false,
        content: [action.payload, ...state.content],
        createError: null,
      };

    case CREATE_REQUEST_FAILURE:
      return {
        ...state,
        creating: false,
        createError: action.payload,
      };

    case SHOW_USER_CREATION_MODAL:
      return {
        ...state,
        showUserCreationModal: true,
      };

    case SHOW_REQUEST_DETAILS_MODAL:
      return {
        ...state,
        showRequestDetailsModal: true,
      };

    case HIDE_REQUEST_DETAILS_MODAL:
      return {
        ...state,
        showRequestDetailsModal: false,
      };
    case SHOW_SEARCH_EMAIL_MODAL:
      return {
        ...state,
        showSearchEmailModal: true,
      };

    case HIDE_SEARCH_EMAIL_MODAL:
      return {
        ...state,
        showSearchEmailModal: false,
      };

    case HIDE_USER_CREATION_MODAL:
      return {
        ...state,
        showUserCreationModal: false,
      };

    case CREATE_ANONYMOUS_REQUEST:
      return {
        ...state,
        anonymousRequest: {
          error: null,
          loading: true,
          requestId: null,
        },
      };

    case CREATE_ANONYMOUS_REQUEST_SUCCESS:
      return {
        ...state,
        anonymousRequest: {
          error: null,
          loading: false,
          requestId: action.payload,
        },
      };

    case CREATE_ANONYMOUS_REQUEST_FAILURE:
      return {
        ...state,
        anonymousRequest: {
          error: action.payload,
          loading: false,
          requestId: null,
        },
      };

    case FETCH_REQUEST_DETAILS_BY_EMAIL:
      return {
        ...state,
        anonymousRequestData: {
          error: null,
          loading: true,
          data: {
            ...defaultAnonymousRequestData,
          },
        },
      };

    case FETCH_REQUEST_DETAILS_BY_EMAIL_SUCCESS:
      return {
        ...state,
        anonymousRequestData: {
          error: null,
          loading: false,
          data: { ...action.payload, currentStep: getStepFromStatus(action.payload.status) },
        },
      };

    case FETCH_REQUEST_DETAILS_BY_EMAIL_FAILURE:
      return {
        ...state,
        anonymousRequestData: {
          error: action.payload,
          loading: false,
          data: { ...defaultAnonymousRequestData },
        },
      };
    case FETCH_ANONYMOUS_AUDITLOG_DETAILS:
      return {
        ...state,
        auditlogs: {
          error: null,
          loading: true,
          data: [],
        },
      };

    case FETCH_ANONYMOUS_AUDITLOG_DETAILS_SUCCESS:
      return {
        ...state,
        auditlogs: {
          error: null,
          loading: false,
          data: action.payload,
        },
      };

    case FETCH_ANONYMOUS_AUDITLOG_DETAILS_FAILURE:
      return {
        ...state,
        auditlogs: {
          error: null,
          loading: true,
          data: [],
        },
      };

    default:
      return state;
  }
};

export default requestReducer;
